import { RootState } from "integrations/redux/rootReducer"
import { createSelector } from "@reduxjs/toolkit"
import { memoize } from "nodash"
import store from "integrations/redux/store"
import { Coordinate, Perimeter, Polygon } from "@nova/protobuf"
import { GeometryType, LineWidth } from "@nova/constants"
import { GeometryWithDistance } from "plugins/shared/layers/types"
import { defaultFirePerimeterColor } from "plugins/DrawPerimeter/constants"
import { setFirePerimeterBuffersForId } from "./buffers/slice"
import { selectAllEntitiesMap } from "panels/content/side-panel/slice"
import { getAncestors } from "panels/content/side-panel/utils"
import { BoundingBoxLayerData } from "plugins/shared/layers/BoundingBoxLayer.class"
import { selectHoveredBoundingBoxId } from "../flights/selectors"
import { generateBufferLinesAsync } from "@nova/calculate-buffers"
import { calculateBoundingBoxCornerPoints } from "@nova/calculate-geospatial"

const root = (state: RootState) => state.project.data.perimeters
const firePerimeterBuffersRoot = (state: RootState) => state["fp-buffers"]

export const selectFirePerimeters = (s: RootState) => root(s)
export const selectNonArchivedFirePerimeters = createSelector(
    [selectFirePerimeters],
    (perimeters) => perimeters.filter((p) => !p.archivedTimestamp)
)

const selectFirePerimeterBuffersMap = (s: RootState) => firePerimeterBuffersRoot(s).byPerimeterId

let lastVisiblePerimeters = [] as Perimeter[]
export const selectVisibleFirePerimeters = createSelector(
    [selectNonArchivedFirePerimeters, selectAllEntitiesMap],
    (perimeters, entityMap) => {
        const nextVisiblePerimietrs = perimeters.filter(
            (e) => e.visible && !getAncestors(e.id, entityMap).find((ancestor) => !ancestor.visible)
        )
        if (
            nextVisiblePerimietrs.length === lastVisiblePerimeters.length &&
            nextVisiblePerimietrs.every((e, i) => lastVisiblePerimeters[i] === e)
        )
            return lastVisiblePerimeters
        lastVisiblePerimeters = nextVisiblePerimietrs
        return nextVisiblePerimietrs
    }
)

export const selectFirePerimetersMap = createSelector([selectFirePerimeters], (perimeters) => {
    return perimeters.reduce(
        (acc, cur) => {
            acc[cur.id] = cur
            return acc
        },
        {} as Record<string, Perimeter>
    )
})
export const selectFirePerimeterById = memoize((perimeterId: string) =>
    createSelector(
        [selectFirePerimetersMap],
        (perimeterMap) => perimeterMap[perimeterId] as Perimeter
    )
)

// const selectFirePerimeterBuffersById = memoize((perimeterId: string) => {
//     const weakObj = new WeakObj(Perimeter.create(), ["buffers", "polygons"])
//     return createSelector([selectFirePerimeterById(perimeterId)], (perimeter) =>
//         perimeter ? weakObj.refFrom(perimeter) : undefined
//     )
// })

let polygonRevision = 1,
    bufferRevision = 1
const polygonKeyByPolygon = new WeakMap()
const bufferKeyByBuffer = new WeakMap()
const bufferCache = new Set<string>()

const bufferDistancesByPerimeterId: Record<string, Set<number>> = {}

export const selectAllFirePerimeterBufferLines = createSelector(
    [selectVisibleFirePerimeters, selectFirePerimeterBuffersMap],
    (perimeters, cachedBuffersMap) =>
        perimeters.flatMap((perimeter) => {
            if (!perimeter || !perimeter.buffers) return []

            let polygonKey = polygonKeyByPolygon.get(perimeter.polygons)
            if (!polygonKey) {
                polygonKeyByPolygon.set(perimeter.polygons, polygonRevision++)
                polygonKey = polygonRevision
            }

            let bufferKey = bufferKeyByBuffer.get(perimeter.buffers)
            if (!bufferKey) {
                bufferKeyByBuffer.set(perimeter.buffers, bufferRevision++)
                bufferKey = bufferRevision
            }

            const cacheKey = polygonKey + ":" + bufferKey

            const distancesChanged = perimeter.buffers.range.some(
                ({ distance }) => !bufferDistancesByPerimeterId[perimeter.id]?.has(distance)
            )

            // If either the top-level polygons or buffers object references changed...
            if (!bufferCache.has(cacheKey) || distancesChanged) {
                // State that buffer lines for this set of polygons and set of buffer distances has already been calculated.
                bufferCache.add(cacheKey)

                // Cache this perimeter's buffer distances.
                if (perimeter.buffers) {
                    bufferDistancesByPerimeterId[perimeter.id] = new Set(
                        perimeter.buffers.range.map(({ distance }) => distance)
                    )
                }

                const generateBuffersForThisPerimeter = async () => {
                    if (!perimeter.buffers) return
                    const bufferData = await generateBufferLinesAsync(
                        perimeter.polygons,
                        perimeter.buffers
                    )
                    const buffersWithDistance = bufferData.flatMap((calculatedBuffers) =>
                        calculatedBuffers.map(
                            (calculatedBuffer) =>
                                ({
                                    ...calculatedBuffer,
                                    geometryType: GeometryType.Polygon,
                                    id: perimeter.id,
                                }) satisfies GeometryWithDistance
                        )
                    )

                    store.dispatch(
                        setFirePerimeterBuffersForId({
                            perimeterId: perimeter.id,
                            data: buffersWithDistance,
                        })
                    )
                }

                generateBuffersForThisPerimeter()
            }

            return cachedBuffersMap[perimeter.id] || []
        })
)

// export const selectFirePerimeterBufferLinesById = memoize((perimeterId: string) =>
//     createSelector([selectFirePerimeterBuffersById(perimeterId)], (perimeter) => {
//         if (!perimeter || !perimeter.buffers) return []
//         return generateBufferLines(perimeter.polygons, perimeter.buffers)
//     })
// )

export const selectAllFirePerimeterBoundingBoxes = createSelector(
    [selectVisibleFirePerimeters, firePerimeterBuffersRoot],
    (perimeters) =>
        perimeters.reduce((boundingBoxes, perimeter) => {
            // All the points of all the polygons in this perimeter, flattened.
            const allPoints = perimeter.polygons.reduce((acc, cur) => {
                cur.coordinates.forEach((coordinate) => acc.push(coordinate))
                return acc
            }, [] as Coordinate[])

            if (allPoints.length > 0) {
                const box = calculateBoundingBoxCornerPoints(allPoints)
                const polygon = box.polygon

                boundingBoxes.push({
                    id: perimeter.id,
                    polygon: box.polygon,
                    path: [...polygon, polygon[0]],
                    area: box.area,
                })
            }
            return boundingBoxes
        }, [] as BoundingBoxLayerData[])
)

export const selectActiveFirePerimeterPolygons = createSelector(
    [selectVisibleFirePerimeters],
    (perimeters) =>
        perimeters.flatMap((perimeter) =>
            perimeter.polygons.map((polygon) => {
                const newPolygon = {
                    ...polygon,
                    color: perimeter.outline || defaultFirePerimeterColor,
                    width: perimeter.width as LineWidth,
                    name: perimeter.name,
                } satisfies Polygon & { color: number; width: LineWidth } & { name: string }

                // Ensure the polygon is closed, repeating the first coordinate at the end.
                if (newPolygon.coordinates.at(0) && newPolygon.coordinates.at(-1)) {
                    const firstCoordinate = newPolygon.coordinates.at(0)
                    const lastCoordinate = newPolygon.coordinates.at(-1)
                    if (
                        firstCoordinate?.longitude !== lastCoordinate?.longitude ||
                        firstCoordinate?.latitude !== lastCoordinate?.latitude
                    ) {
                        newPolygon.coordinates = [
                            ...newPolygon.coordinates,
                            { ...newPolygon.coordinates[0] },
                        ]
                    }
                }

                return newPolygon
            })
        )
)

export const selectActiveFirePerimeterIds = createSelector(
    [selectVisibleFirePerimeters],
    (perimeters) => perimeters.map((e) => e.id)
)

export const selectNextFirePerimeterNumber = createSelector(
    [selectFirePerimeters],
    (perimeters) => perimeters.length + 1
)

export const selectHoveredFirePerimeterId = createSelector(
    [selectNonArchivedFirePerimeters, selectHoveredBoundingBoxId],
    (perimeters, id) => (perimeters.some((perimeter) => perimeter.id === id) ? id : undefined)
)
